import { Permission } from '../../model/permission/Permission';
import i18n from "../../i18n";

const API_URL = process.env.REACT_APP_BUSINESS_SERVICE_BASE_URL || '';

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors.map((e: { message: string }) => e.message).join(', ');
        throw new Error(msg);
    }
    return json.data as T;
}

/** The caller's resolved grant for a permission — drives own/all UI scoping
 *  (e.g. restricting the calendar to a LIMITED user's own appointments). */
export interface PermissionGrant {
    /** Permission key (e.g. "CALENDAR") — present in myPermissions. */
    permissionKey?: string | null;
    level: string;
    /** Caller's team_member id (own-scope); null for admins/owners. */
    memberId?: string | null;
    canView: boolean;
    canEdit: boolean;
    /** True when access spans the whole business (not just own records). */
    allScope: boolean;
}

/** All of the caller's effective permission grants for a business (menu gating). */
export async function getMyPermissions(
    businessId: number | string,
    token: string
): Promise<PermissionGrant[]> {
    const data = await gqlPost<{ myPermissions: PermissionGrant[] | null }>(
        token,
        `query myPermissions($businessId: ID!) {
            myPermissions(businessId: $businessId) {
                permissionKey level memberId canView canEdit allScope
            }
        }`,
        { businessId }
    );
    return data.myPermissions ?? [];
}

/** The max discount rate (%) the caller may apply for this business. */
export async function getMyMaxDiscountRate(
    businessId: number | string,
    token: string
): Promise<number> {
    const data = await gqlPost<{ myMaxDiscountRate: number }>(
        token,
        `query myMaxDiscountRate($businessId: ID!) { myMaxDiscountRate(businessId: $businessId) }`,
        { businessId }
    );
    return data.myMaxDiscountRate ?? 0;
}

export async function getMyPermissionGrant(
    businessId: number | string,
    permissionKey: string,
    token: string
): Promise<PermissionGrant> {
    const data = await gqlPost<{ myPermissionGrant: PermissionGrant }>(
        token,
        `query myPermissionGrant($businessId: ID!, $permissionKey: String!) {
            myPermissionGrant(businessId: $businessId, permissionKey: $permissionKey) {
                level memberId canView canEdit allScope
            }
        }`,
        { businessId, permissionKey }
    );
    return data.myPermissionGrant;
}

export async function getAllPermissions(token: string): Promise<Permission[]> {
    const data = await gqlPost<{ getAllPermissions: Permission[] | null }>(
        token,
        `query {
            getAllPermissions {
                id
                permissionKey
                name
                description
                type
                groupName
                displayOrder
                options { id optionName description }
            }
        }`
    );
    return data.getAllPermissions ?? [];
}
